// src/components/mpi/CsvWorkItemPresetImporterModal.js
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { useDispatch } from 'react-redux';
import { addWorkItemPreset } from '../../actions/apiWorkItemPresets';
import { 
  Box, Button, Alert, Table, TableBody, TableCell, TableHead, TableRow, 
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions 
} from '@mui/material';

const columnMapping = {
  'Name': 'name',
  'Description': 'description',
  'Category': 'category',
  'Estimate Type': 'estimate_type',
  'Parts Price': 'parts_price',
  'Labor Price': 'labor_price',
  'Labor Units': 'labor_units',
  'Estimate Total': 'estimate_total',
  'MPI Order': 'order',
  'Active': 'active',
  'Deleted': 'deleted',
  'Dealership': 'dealership'
};

const CsvWorkItemPresetImporterModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(0);
  const [errors, setErrors] = useState([]);

  const reset = () => {
    setCsvData([]);
    setHeaders([]);
    setPreview([]);
    setUploading(false);
    setSuccess(0);
    setErrors([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      if (typeof text !== 'string') {
        alert('Invalid file');
        return;
      }

      Papa.parse(text, {
        complete: (results) => {
          const { data } = results;
          if (data.length < 2) {
            alert('CSV must have headers + 1 row');
            return;
          }

          const headerRow = data[0];
          const rows = data.slice(1).filter(row => row.some(cell => cell?.trim() !== ''));

          setHeaders(headerRow);
          setCsvData(rows);
          setPreview(rows.slice(0, 5).map(row => {
            const obj = {};
            headerRow.forEach((h, i) => {
              obj[h] = row[i] || '';
            });
            return obj;
          }));
        },
        header: false,
        skipEmptyLines: true,
      });
    };
    reader.onerror = () => alert('Failed to read file');
    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'text/csv',
    multiple: false,
  });

  const importAll = async () => {
    setUploading(true);
    setSuccess(0);
    setErrors([]);

    const results = [];
    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      const preset = {};

      headers.forEach((header, idx) => {
        const key = columnMapping[header];
        if (key) {
          let value = row[idx]?.trim() || '';

          if (key === 'active' || key === 'deleted') {
            value = ['true', '1', 'True', 'TRUE'].includes(value);
          } else if (['parts_price', 'labor_price', 'labor_units', 'estimate_total', 'order'].includes(key)) {
            value = parseFloat(value) || 0;
          } else {
            value = value;
          }

          preset[key] = value;
        }
      });

      preset.show_mpi = true;
      preset.layout_type = 
        preset.estimate_type?.toLowerCase().includes('tire') || 
        preset.estimate_type?.toLowerCase().includes('brake') 
          ? '4_wheel_input' 
          : '';
      preset.active = preset.active !== false;
      preset.deleted = preset.deleted === true;

      try {
        await dispatch(addWorkItemPreset(preset));
        results.push({ success: true, name: preset.name });
      } catch (err) {
        results.push({ success: false, name: preset.name, error: err.message || 'Failed' });
      }
    }

    const successes = results.filter(r => r.success).length;
    const fails = results.filter(r => !r.success);

    setSuccess(successes);
    setErrors(fails);
    setUploading(false);

    // Auto-close after 2 seconds if all good
    if (fails.length === 0 && successes > 0) {
      setTimeout(handleClose, 2000);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <strong>Mass Import Work Item Presets</strong>
        <Button onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          ×
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {csvData.length === 0 ? (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
              '&:hover': { backgroundColor: '#f0f8ff' }
            }}
          >
            <input {...getInputProps()} />
            <p style={{ margin: 0, fontSize: '1.2rem' }}>
              {isDragActive ? 'Drop CSV here' : 'Drag & drop CSV or click to select'}
            </p>
            <p style={{ margin: '10px 0 0', fontSize: '0.9rem', color: '#666' }}>
              Columns: Name, Description, Category, Estimate Type, Parts Price, Labor Price, Labor Units, Estimate Total, MPI Order, Active, Deleted, Dealership
            </p>
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Found {csvData.length} rows. Preview (first 5):
            </Alert>

            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  {headers.map((h, i) => (
                    <TableCell key={i}><strong>{h}</strong></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((row, i) => (
                  <TableRow key={i}>
                    {headers.map((h, j) => (
                      <TableCell key={j}>{row[h]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {uploading && <LinearProgress sx={{ mb: 2 }} />}

            {success > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully imported {success} presets!
              </Alert>
            )}

            {errors.length > 0 && (
              <Alert severity="error">
                <strong>{errors.length} failed:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  {errors.map((e, i) => (
                    <li key={i}>{e.name}: {e.error}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      {csvData.length > 0 && (
        <DialogActions>
          <Button variant="outlined" onClick={() => setCsvData([])}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={importAll} 
            disabled={uploading}
          >
            {uploading ? 'Importing...' : `Import ${csvData.length} Presets`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CsvWorkItemPresetImporterModal;